import React from 'react';
import { flexRender } from '@tanstack/react-table';
import { StyledTableContainer, StyledTable, StyledTr, StyledTd, StyledInnerTd, StyledContainer } from './styles';
import TableHeader from './TableHeader';
import Pagination from './Pagination';
import { BaseTableProps } from './types';
import { getCommonPinningStyles } from './utils/getCommonPinningStyles';
import { useTableConfig } from './hooks/useTableConfig';

// TODO: columnPinning을 columns에서 처리하는 방안을 고려

/**
 * TanstackControlledTable 컴포넌트는 정렬, 필터링, 고정 열 기능, 페이지네이션을 **외부 상태**로 관리하는 테이블을 렌더링합니다.
 * @template T - 테이블 데이터 타입
 * @param props - 컴포넌트 속성
 * @returns - 렌더링된 테이블 컴포넌트
 */
const TanstackControlledTable = <T,>({
  data,
  columns: originColumns,
  sortingState,
  filteringState,
  rowSelectionState,
  paginationState,
  columnPinning = [],
  rowCount,
}: BaseTableProps<T>): JSX.Element => {
  const { table } = useTableConfig({
    data,
    columns: originColumns,
    sortingState,
    filteringState,
    rowSelectionState,
    paginationState,
    columnPinning,
    rowCount,
  });

  return (
    <StyledContainer>
      <StyledTableContainer>
        <StyledTable width={table.getTotalSize()}>
          <TableHeader headerGroups={table.getHeaderGroups()} />
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <StyledTr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <StyledTd key={cell.id} {...getCommonPinningStyles(cell.column)}>
                    <StyledInnerTd>{flexRender(cell.column.columnDef.cell, cell.getContext())}</StyledInnerTd>
                  </StyledTd>
                ))}
              </StyledTr>
            ))}
          </tbody>
        </StyledTable>
      </StyledTableContainer>
      <Pagination
        hasPrev={table.getCanPreviousPage()}
        hasNext={table.getCanNextPage()}
        onPrev={(): void => {
          table.previousPage();
        }}
        onNext={(): void => {
          table.nextPage();
        }}
        onPageSelect={(e): void => {
          table.setPageSize(Number(e.target.value));
        }}
        onEnter={(e): void => {
          if (e.key === 'Enter') {
            const page = e.currentTarget.value ? Number(e.currentTarget.value) - 1 : 0;

            if (page + 1 > table.getPageCount()) {
              return;
            }

            table.setPageIndex(page);
          }
        }}
        pageSize={table.getState().pagination.pageSize}
        maxPageSize={table.getPageCount() ? table.getPageCount() : 1}
        pageIndex={table.getState().pagination.pageIndex}
      />
    </StyledContainer>
  );
};

export default TanstackControlledTable;
